import { useState } from 'react';
import { useT } from '../i18n/i18n';
import { AREA_LABEL, GOAL_STATUS_LABEL, SOURCE_LABEL, createGoal, updateGoal, type Area, type Goal, type GoalSource, type GoalStatus } from './api';
import { orNull } from './GoalDates';
import DateField from '../components/DateField';

export const GAS_LEVELS: { key: string; sign: string; label: string }[] = [
  { key: '-2', sign: '−2', label: 'Much less than expected' },
  { key: '-1', sign: '−1', label: 'Somewhat less than expected' },
  { key: '0', sign: '0', label: 'As expected' },
  { key: '1', sign: '+1', label: 'Somewhat more than expected' },
  { key: '2', sign: '+2', label: 'Much more than expected' },
];

export default function GoalEditor({ learnerId, goal, onSaved, onCancel, onDelete }: {
  learnerId: string; goal: Goal | null; onSaved: () => void; onCancel: () => void; onDelete?: () => void;
}) {
  const t = useT();
  const [statement, setStatement] = useState(goal?.statement ?? '');
  const [area, setArea] = useState<Area>(goal?.area ?? 'COMMUNICATION');
  const [source, setSource] = useState<GoalSource>(goal?.source ?? 'FAMILY');
  const [byDate, setByDate] = useState(goal?.byDate ?? '');
  const [baseline, setBaseline] = useState(goal?.baseline ?? '');
  const [gas, setGas] = useState<Record<string, string>>(goal?.gas ?? {});
  const [status, setStatus] = useState<GoalStatus>(goal?.status ?? 'WORKING');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const id = goal?.id ?? 'new';

  async function save() {
    setBusy(true);
    setError(null);
    const levels: Record<string, string> = {};
    for (const l of GAS_LEVELS) {
      const v = (gas[l.key] ?? '').trim();
      if (v) levels[l.key] = v;
    }
    const body = {
      statement: statement.trim(), area, source, byDate: byDate || null, baseline: orNull(baseline),
      gas: Object.keys(levels).length ? levels : null, status,
    };
    try {
      if (goal) await updateGoal(learnerId, goal.id, body);
      else await createGoal(learnerId, body);
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : t('That could not be saved just now.'));
      setBusy(false);
    }
  }

  return (
    <div className="planner-goal-editor">
      <h3>{goal ? t('Edit goal') : t('New goal')}</h3>
      <div className="field">
        <label htmlFor={`goal-statement-${id}`}>{t('What we want')}</label>
        <textarea id={`goal-statement-${id}`} rows={3} maxLength={2000} value={statement} onChange={(e) => setStatement(e.target.value)} />
        <p className="field-hint">{t("The difference you want to see in your learner's life, in plain words.")}</p>
      </div>
      <div className="planner-goal-fields">
        <div className="field">
          <label htmlFor={`goal-area-${id}`}>{t('Area of need')}</label>
          <select id={`goal-area-${id}`} value={area} onChange={(e) => setArea(e.target.value as Area)}>
            {(Object.keys(AREA_LABEL) as Area[]).map((a) => <option key={a} value={a}>{t(AREA_LABEL[a])}</option>)}
          </select>
        </div>
        <div className="field">
          <label htmlFor={`goal-source-${id}`}>{t('Where it comes from')}</label>
          <select id={`goal-source-${id}`} value={source} onChange={(e) => setSource(e.target.value as GoalSource)}>
            {(Object.keys(SOURCE_LABEL) as GoalSource[]).map((s) => <option key={s} value={s}>{t(SOURCE_LABEL[s])}</option>)}
          </select>
        </div>
        <div className="field">
          <label htmlFor={`goal-by-${id}`}>{t('By when')}</label>
          <DateField id={`goal-by-${id}`} value={byDate} onChange={(v) => setByDate(v ?? '')} />
        </div>
        <div className="field">
          <label htmlFor={`goal-status-${id}`}>{t('Status')}</label>
          <select id={`goal-status-${id}`} value={status} onChange={(e) => setStatus(e.target.value as GoalStatus)}>
            {(Object.keys(GOAL_STATUS_LABEL) as GoalStatus[]).map((s) => <option key={s} value={s}>{t(GOAL_STATUS_LABEL[s])}</option>)}
          </select>
        </div>
      </div>
      <div className="field">
        <label htmlFor={`goal-baseline-${id}`}>{t('What happens now')}</label>
        <textarea id={`goal-baseline-${id}`} rows={2} maxLength={2000} value={baseline} onChange={(e) => setBaseline(e.target.value)} />
      </div>
      <details className="planner-goal-gas-editor" open={GAS_LEVELS.some((l) => gas[l.key])}>
        <summary>{t('Goal-attainment levels (optional)')}</summary>
        <p className="field-hint">{t('Describe what each result would look like. Leave any you do not need empty.')}</p>
        {GAS_LEVELS.map((l) => (
          <div className="field" key={l.key}>
            <label htmlFor={`goal-gas-${l.key}-${id}`}>{l.sign} {t(l.label)}</label>
            <input id={`goal-gas-${l.key}-${id}`} type="text" maxLength={500} value={gas[l.key] ?? ''} onChange={(e) => setGas({ ...gas, [l.key]: e.target.value })} />
          </div>
        ))}
      </details>
      {error && <p className="error" role="alert">{error}</p>}
      <div className="form-actions">
        <button type="button" className="btn" disabled={busy || !statement.trim()} onClick={() => void save()}>{t('Save goal')}</button>
        <button type="button" className="btn secondary" disabled={busy} onClick={onCancel}>{t('Cancel')}</button>
        {onDelete && <button type="button" className="btn quiet-danger planner-goal-delete" disabled={busy} onClick={onDelete}>{t('Delete this goal')}</button>}
      </div>
    </div>
  );
}
